"use client";
import { styled } from "styled-components";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import {
  ActionCard,
  BodyText,
  breakpointDesktop,
  breakpointMobile,
  breakpointTablet,
  Icon,
} from "@gnist/design-system";
import { Car, CarImageType } from "@/data/bruktbilsalgBackend/types/car.types";
import { CarImagesModal } from "./carImagesModal";
import { ImageContainer } from "@/components/styled-common/imageContainer";
import defaultImageNormal from "@/public/images/defaultCar-normal.jpg";
import { getSortedCarImages } from "@/utils/getSortedCarImages";
import { tokens } from "@gnist/themes/tokens.css.js";

const StyledActionCard = styled(ActionCard)`
  max-width: 1120px;
  padding: 0;
  background-color: transparent;
  box-shadow: unset;

  &:disabled {
    cursor: auto;
  }
  &:hover:not(:disabled) {
    box-shadow: ${tokens.elevation.low};
  }
`;

const Wrapper = styled.div`
  @media (min-width: ${breakpointTablet}) {
    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: repeat(2, 1fr);

    grid-gap: ${tokens.spacing.xxs};
  }

  @media screen and (max-width: ${breakpointMobile}) {
    position: relative;

    display: flex;
    justify-content: center;

    width: 100vw;

    margin-left: calc(
      ${tokens.spacing.l} * -1
    ); // override padding from main html-tag
  }
`;

const AllImagesIconWrapper = styled.div`
  display: flex;
  position: absolute;
  right: ${tokens.spacing.s};
  bottom: ${tokens.spacing.s};
  align-content: center;
  gap: 6px;
  padding: ${tokens.spacing.xxs} ${tokens.spacing.m};
  border-radius: ${tokens.radius.card};
  background-color: ${tokens.color["primary-container"]};
  color: ${tokens.color["on-primary-container"]};

  svg path {
    fill: ${tokens.color["on-primary-container"]};
  }

  @media (max-width: ${breakpointDesktop}) {
    right: ${tokens.spacing.xs};
    bottom: ${tokens.spacing.xs};
    padding: 6px ${tokens.spacing.s};
  }
`;

const StyledActionText = styled(BodyText)`
  font-size: 16px;
  font-weight: ${tokens.typeface.weight.semibold};

  @media (max-width: ${breakpointDesktop}) {
    font-size: 14px;
  }
`;

const MainImageContainer = styled(ImageContainer)`
  grid-area: 1 / 1 / 3 / 2;
`;

const SecondaryImageContainer = styled(ImageContainer)`
  @media (max-width: ${breakpointTablet}) {
    display: none;
  }
`;

const ViewAllImages = ({ numCars }: { numCars: number }) => {
  return (
    <AllImagesIconWrapper>
      <Icon size={"m"} icon={"image_icon"} />
      <StyledActionText>Se alle bilder ({numCars})</StyledActionText>
    </AllImagesIconWrapper>
  );
};

type props = {
  car: Car;
};

export const CarImages = ({ car }: props) => {
  const [showAllImages, setShowAllImages] = useState<boolean>(false);
  const [inactiveActionCard, setInactiveActionCard] = useState(false);
  const [isFirstImageUrlValid, setIsFirstImageUrlValid] =
    useState<boolean>(true);
  const [isSecondImageUrlValid, setIsSecondImageUrlValid] =
    useState<boolean>(true);
  const [isThirdImageUrlValid, setIsThirdImageUrlValid] =
    useState<boolean>(true);

  const hasImages = !!car.carImages?.length;

  const images: CarImageType[] = hasImages
    ? getSortedCarImages(car.carImages!)
    : [
        {
          normalUrl: defaultImageNormal.src,
          thumbnailUrl: "",
          sortOrder: 0,
        },
      ];

  const firstImageUrl = images[0].normalUrl;
  const secondImageUrl = images[1] ? images[1].normalUrl : images[0].normalUrl;
  const thirdImageUrl = images[2] ? images[2].normalUrl : images[0].normalUrl;

  useEffect(() => {
    const validImageUrls =
      isFirstImageUrlValid || isSecondImageUrlValid || isThirdImageUrlValid;

    setInactiveActionCard(!hasImages || !validImageUrls);
  }, [
    hasImages,
    isFirstImageUrlValid,
    isSecondImageUrlValid,
    isThirdImageUrlValid,
  ]);

  return (
    <>
      <CarImagesModal
        car={car}
        showAllImages={showAllImages}
        setShowAllImages={setShowAllImages}
      />

      <StyledActionCard
        disabled={inactiveActionCard}
        onClick={() => setShowAllImages(true)}
      >
        <Wrapper>
          <MainImageContainer>
            <Image
              src={isFirstImageUrlValid ? firstImageUrl : defaultImageNormal}
              loader={(img) => img.src}
              unoptimized={true}
              alt={`Bilde av ${car.brand} ${car.model}`}
              fill={true}
              onError={() => setIsFirstImageUrlValid(false)}
              sizes={"100%, 100%"}
              style={{ objectFit: "cover" }}
              priority={true}
            />
          </MainImageContainer>
          <SecondaryImageContainer>
            <Image
              src={isSecondImageUrlValid ? secondImageUrl : defaultImageNormal}
              loader={(img) => img.src}
              unoptimized={true}
              alt={`Bilde av ${car.brand} ${car.model}`}
              fill={true}
              onError={() => setIsSecondImageUrlValid(false)}
              sizes={"100%, 100%"}
              style={{ objectFit: "cover" }}
            />
          </SecondaryImageContainer>
          <SecondaryImageContainer>
            <Image
              src={isThirdImageUrlValid ? thirdImageUrl : defaultImageNormal}
              loader={(img) => img.src}
              unoptimized={true}
              alt={`Bilde av ${car.brand} ${car.model}`}
              fill={true}
              onError={() => setIsThirdImageUrlValid(false)}
              sizes={"100%, 100%"}
              style={{ objectFit: "cover" }}
            />
          </SecondaryImageContainer>
          {car.carImages && car.carImages.length > 0 && (
            <ViewAllImages numCars={car.carImages.length} />
          )}
        </Wrapper>
      </StyledActionCard>
    </>
  );
};
